import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { PublicUser } from '@rabbitfuel/shared';
import { reportGateLocked } from './gate-signal.ts';

const TOKEN_KEY = 'rabbitfuel:token';

export const readToken = (): string | null => {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
};

const writeToken = (token: string | null) => {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* Private Mode -- dann gilt die Anmeldung nur fuer diese Seite. */
  }
};

/** Fetch mit Sitzungstoken, falls vorhanden. */
export async function authFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const token = readToken();
  const res = await fetch(path, {
    ...init,
    headers: {
      // Nur mit Inhalt -- sonst lehnt Fastify ab (siehe `req` in api.ts).
      ...(init?.body !== undefined ? { 'content-type': 'application/json' } : {}),
      ...(token ? { authorization: `Bearer ${token}` } : {}),
      ...(init?.headers ?? {}),
    },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    reportGateLocked(res.status, body);
    throw new Error((body as { error?: string }).error ?? `HTTP ${res.status}`);
  }
  return (await res.json()) as T;
}

interface AuthApi {
  user: PublicUser | null;
  /** true, solange die gespeicherte Anmeldung noch geprueft wird. */
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, displayName: string) => Promise<void>;
  logout: () => Promise<void>;
}

const Ctx = createContext<AuthApi | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<PublicUser | null>(null);
  const [loading, setLoading] = useState(true);

  // Gespeichertes Token beim Start gegen den Server pruefen.
  useEffect(() => {
    if (!readToken()) {
      setLoading(false);
      return;
    }
    authFetch<{ user: PublicUser }>('/api/auth/me')
      .then((r) => setUser(r.user))
      .catch(() => {
        writeToken(null);
        setUser(null);
      })
      .finally(() => setLoading(false));
  }, []);

  const accept = (r: { user: PublicUser; token: string }) => {
    writeToken(r.token);
    setUser(r.user);
  };

  const login = useCallback(async (email: string, password: string) => {
    accept(
      await authFetch<{ user: PublicUser; token: string }>('/api/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      }),
    );
  }, []);

  const register = useCallback(async (email: string, password: string, displayName: string) => {
    accept(
      await authFetch<{ user: PublicUser; token: string }>('/api/auth/register', {
        method: 'POST',
        body: JSON.stringify({ email, password, displayName }),
      }),
    );
  }, []);

  const logout = useCallback(async () => {
    await authFetch('/api/auth/logout', { method: 'POST' }).catch(() => undefined);
    writeToken(null);
    setUser(null);
  }, []);

  const api = useMemo<AuthApi>(() => ({ user, loading, login, register, logout }), [user, loading, login, register, logout]);
  return <Ctx.Provider value={api}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthApi {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useAuth muss innerhalb von <AuthProvider> genutzt werden.');
  return ctx;
}
